/****************************************************
 * Module Name:  ConsigneeMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import React, { useState, useEffect } from "react";
import renderCellExpand from "../common/dataGridCellExpand";
import { Box, Tooltip, Button } from "@mui/material";
import CommonSnackbar from "../common/commonSnackBar";
import Constants from "../../utility/constants";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import NoPermission from "../common/noPermission";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import AddConsigneeMasterModal from "./addConsigneeMasterModal";
import ConsigneeMasterProxy from "../../utility/proxy/nivodautility/consigneeMasterProxy";
import UserProxy from "../../utility/proxy/manager/userProxy";
import Utility from "../../utility/utility";
import PageTitle from "../common/pageTitle";


const ConsigneeMaster = () => {

    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [consigneeList, setConsigneeList] = useState([]);
    const [access, setAccess] = useState(false);
    const [consigneeListLoader, setConsigneeListLoader] = useState(true);
    const [sourceData, setSourceData] = useState([]);
    const [editOperation, setEditOperation] = useState(false);
    const [showAddConsigneeModal, setShowAddConsigneeModal] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const [consigneeOperation, setConsigneeOperation] = useState('create');
    const [consigneeSuccessSnackbar, setConsigneeSuccessSnackbar] = useState(false);
    const [userOptions, setUserOptions] = useState([]);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [selectedUser, setSelectedUser] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState([]);
    const [filterValues, setFilterValues] = useState({});

    useEffect(() => {
        PageTitle("ANALYSIS_WOLZA_CONSIGNEEMASTER");
        checkPermission();
    }, []);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.ANALYSIS_WOLZA_CONSIGNEEMASTER
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        } else {
            fetchConsigneeData();
        }
    }

    const handleValidation = (consigneeData) => {
        //TODO do validation if needed here
        return true;
    };

    const fetchConsigneeData = (bAnimateSpinner) => {
        bAnimateSpinner && setConsigneeListLoader(true);
        ConsigneeMasterProxy.GetAllConsignee(
            "",
            (responseData) => {
                if (responseData) {
                    const uniqueFilterValues = {
                        countryofdestination: Utility.getDistinctOptions(responseData, "countryofdestination"),
                        mappedtocompany: Utility.getDistinctOptions(responseData, "mappedtocompany"),
                        mappedtouser: Utility.getDistinctOptions(responseData, "mappedtouser"),
                        updatedbyuser: Utility.getDistinctOptions(responseData, "updatedbyuser"),
                        mappedtoemployee: Utility.getDistinctOptions(responseData, "mappedtoemployee"),
                        updatedbycompany: Utility.getDistinctOptions(responseData, "updatedbycompany"),
                    };
                    const combinedFilterValues = {
                        mappedtocompany: uniqueFilterValues.mappedtocompany.map(value => {
                            const matchingItem = responseData.find(item => item.mappedtocompany === value);
                            if (matchingItem) {
                                let displayValue = matchingItem.mappedtoid && matchingItem.mappedtocompany ? `${matchingItem.mappedtoid} | ${matchingItem.mappedtocompany}` : '';
                                if (displayValue && matchingItem.mappedtoemployee) {
                                    displayValue += ` | ${matchingItem.mappedtoemployee}`;
                                }
                                return displayValue;
                            }
                            return [];
                        }),
                        mappedtouser: uniqueFilterValues.mappedtouser.map(value => {
                            const matchingItem = responseData.find(item => item.mappedtouser === value);
                            if (matchingItem) {
                                let displayValue = matchingItem.mappedtoid && matchingItem.mappedtouser ? `${matchingItem.mappedtoid} | ${matchingItem.mappedtouser}` : '';
                                if (displayValue && matchingItem.mappedtoemployee) {
                                    displayValue += ` | ${matchingItem.mappedtoemployee}`;
                                }
                                return displayValue;
                            }
                            return [];
                        }),
                        updatedbyuser: uniqueFilterValues.updatedbyuser.map(value => {
                            const matchingItem = responseData.find(item => item.updatedbyuser === value);
                            return matchingItem && matchingItem.updatedbyid ? `${matchingItem.updatedbyid} | ${value}` : '';
                        }),
                        countryofdestination: uniqueFilterValues.countryofdestination,
                        mappedtoemployee: uniqueFilterValues.mappedtoemployee,
                        updatedbycompany: uniqueFilterValues.updatedbycompany,
                    };
                    setFilterValues(combinedFilterValues);
                    // Sort responseData by consigneename (A-Z and then numbers) as discussed with ajay 
                    responseData.sort((a, b) => {
                        const nameA = a.consigneename || "";
                        const nameB = b.consigneename || "";

                        const isAlphaA = /^[a-zA-Z]/.test(nameA);
                        const isAlphaB = /^[a-zA-Z]/.test(nameB);

                        if (isAlphaA && !isAlphaB) return -1;
                        if (!isAlphaA && isAlphaB) return 1;
                        return nameA.localeCompare(nameB);
                    });

                    setConsigneeList(responseData);
                    getAllMappedUserData(true);
                    setConsigneeListLoader(false);
                } else {
                    setErrorSnackbar(true);
                    setConsigneeListLoader(false);
                }
            }
        );
    };

    const getAllMappedUserData = (bAnimateSpinner) => {
        UserProxy.getAllCustomerMappedEmployeeMini((response) => {
            if (response)
                setAllMappedUserData(response)
            else {
                setErrorSnackbar(true);
            }
        });
    };

    const setAllMappedUserData = (res) => {
        if (res) {
            let companyOptionsArray = [];
            let userOptionsArray = [];
            res.sort((a, b) => a.mappedtocompany.toLowerCase().localeCompare(b.mappedtocompany.toLowerCase())).forEach(data => {
                if (data.mappedtoid && data.mappedtocompany) {
                    let companyLabel = `${data.mappedtoid} | ${data.mappedtocompany}`;
                    if (data.mappedtoemployee) {
                        companyLabel += ` | ${data.mappedtoemployee}`;
                    }
                    let companyOption = {
                        value: data.mappedtoid,
                        label: companyLabel
                    };
                    companyOptionsArray.push(companyOption);
                }
            });
            setCompanyOptions(companyOptionsArray);
            res.sort((a, b) => a.mappedtouser.toLowerCase().localeCompare(b.mappedtouser.toLowerCase())).forEach(data => {
                if (data.mappedtoid && data.mappedtouser) {
                    let userLabel = `${data.mappedtoid} | ${data.mappedtouser}`;
                    if (data.mappedtoemployee) {
                        userLabel += ` | ${data.mappedtoemployee}`;
                    }
                    let userOption = {
                        value: data.mappedtoid,
                        label: userLabel
                    };
                    userOptionsArray.push(userOption);
                }
            });
            setUserOptions(userOptionsArray);
            if (userOptionsArray.length > 0) {
                setSelectedUser(userOptionsArray[0]);
            }
            if (companyOptionsArray.length > 0) {
                setSelectedCompany(companyOptionsArray[0]);
            }
        }
    }

    const addOrUpdateConsignee = (consigneeData) => {
        setLoadingButton(true);
        setConsigneeOperation(editOperation ? 'update' : 'create');
        if (editOperation) {
            ConsigneeMasterProxy.UpdateConsignee(consigneeData, (response) => {
                if (response) {
                    fetchConsigneeData(true);
                    setLoadingButton(false);
                    setShowAddConsigneeModal(false);
                    setConsigneeSuccessSnackbar(true);
                } else {
                    setErrorSnackbar(true);
                    setLoadingButton(false);
                }
            });
        } else {
            ConsigneeMasterProxy.AddConsignee(consigneeData, (response) => {
                if (response) {
                    fetchConsigneeData(true);
                    setLoadingButton(false);
                    setShowAddConsigneeModal(false);
                    setConsigneeSuccessSnackbar(true);
                } else {
                    setErrorSnackbar(true);
                    setLoadingButton(false);
                }
            });
        }
    };

    const handleClose = () => {
        setConsigneeSuccessSnackbar(false);
    };

    const initialColumns = () => [
        {
            field: "consigneeid",
            headerName: "CONSIGNEE ID",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "consigneename",
            headerName: "WOLZA CONSIGNEE NAME",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "countryofdestination",
            headerName: "COUNTRY OF DESTINATION",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.countryofdestination : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "mappedtoid",
            headerName: "MAPPED TO ID",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "mappedtocompany",
            headerName: "MAPPED TO COMPANY",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.mappedtocompany : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => {
                const { mappedtoid, mappedtocompany, mappedtoemployee } = params.row;
                let displayValue = mappedtoid && mappedtocompany ? `${mappedtoid} | ${mappedtocompany}` : '';
                if (displayValue && mappedtoemployee) {
                    displayValue += ` | ${mappedtoemployee}`;
                }
                return displayValue;
            },
            renderCell: renderCellExpand
        },
        {
            field: "mappedtouser",
            headerName: "MAPPED TO USER",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.mappedtouser : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => {
                const { mappedtoid, mappedtouser, mappedtoemployee } = params.row;
                let displayValue = mappedtoid && mappedtouser ? `${mappedtoid} | ${mappedtouser}` : '';
                if (displayValue && mappedtoemployee) {
                    displayValue += ` | ${mappedtoemployee}`;
                }
                return displayValue;
            },
            renderCell: renderCellExpand
        },
        {
            field: "mappedtoemployeeid",
            headerName: "MAPPED TO EMPLOYEEID",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "mappedtoemployee",
            headerName: "MAPPED TO EMPLOYEE",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.mappedtoemployee : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "updatedbyid",
            headerName: "UPDATED BY ID",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "updatedbycompany",
            headerName: "UPDATED BY COMPANY",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.updatedbycompany : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "updatedbyuser",
            headerName: "UPDATED BY USER",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.updatedbyuser : [],
            filterOperators: Utility.combinedFilters(),
            valueGetter: (params) => {
                const { updatedbyid, updatedbyuser } = params.row;
                return updatedbyid && updatedbyuser ? `${updatedbyid} | ${updatedbyuser}` : '';
            },
            renderCell: renderCellExpand
        },
        {
            field: "edit",
            headerName: "EDIT",
            headerAlign: "center",
            align: "center",
            flex: 0.5,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const rowData = { ...params.row };
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            setEditOperation(true)
                            setShowAddConsigneeModal(true)
                            setSourceData(rowData)
                            setSelectedUser(userOptions.find(option => option.value === rowData.mappedtoid))
                            setSelectedCompany(companyOptions.find(option => option.value === rowData.mappedtoid))
                        }}
                    >
                        {" "}
                        <BorderColorIcon color="primary" />
                    </Box>
                );
            },
        },
    ];

    const columns = initialColumns();
    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
                {consigneeListLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            color="primary"
                            fontSize="medium"
                            onClick={() => fetchConsigneeData()}
                            sx={{ cursor: "pointer" }}
                        />
                    </Tooltip>
                )}
                <Tooltip title="Add Consignee">
                    <Button
                        color="info"
                        variant="outlined"
                        size="small"
                        style={{ marginRight: "10px", marginLeft: "10px" }}
                        onClick={() => {
                            setEditOperation(false)
                            setShowAddConsigneeModal(true)
                            setSourceData([])
                            setSelectedUser(userOptions[0])
                            setSelectedCompany(companyOptions[0])
                        }
                        }
                        startIcon={<AddBoxOutlinedIcon size="large" />}
                    >
                        Consignee
                    </Button>
                </Tooltip>
                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        );
    }
    return (
        <Box>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />

            {consigneeSuccessSnackbar ? (
                <CommonSnackbar
                    show={consigneeSuccessSnackbar}
                    message={`Consignee ${consigneeOperation === 'update' ? 'updated' : 'created'} successfully`}
                    variant={"success"}
                    autoHide={true}
                    title={"success"}
                    handleClose={handleClose}
                />
            ) : null}

            {showAddConsigneeModal ? (
                <AddConsigneeMasterModal
                    Show={true}
                    handleClose={() => setShowAddConsigneeModal(false)}
                    handleSave={(data) => addOrUpdateConsignee(data)}
                    handleValidation={() => handleValidation()}
                    editOperation={editOperation}
                    sourceData={sourceData}
                    loading={loadingButton}
                    userOptions={userOptions}
                    companyOptions={companyOptions}
                    selectedUser={selectedUser}
                    selectedCompany={selectedCompany}
                />
            ) : null}

            {
                access ? <NoPermission /> : (
                    <Box px={1} mt={4}>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                height: "85vh",
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                density="compact"
                                loading={consigneeListLoader}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },
                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                slots={{ toolbar: CustomToolbar }}
                                rows={consigneeList}
                                columns={columns}
                                getRowId={(row) => row.consigneeid}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns status and traderName, the other columns will remain visible
                                            consigneeid: false,
                                            mappedtoid: false,
                                            updatedbyid: false,
                                            updatedbycompany: false,
                                            mappedtoemployeeid: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                )
            }
        </Box >
    )
}

export default ConsigneeMaster;